import React, { useState, useEffect } from "react";
import {
  InboxOutlined,
  UploadOutlined,
  FolderOutlined,
  FileImageOutlined,
  FileTextOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined,
  SettingFilled,
} from "@ant-design/icons";
import { Button, Upload, Progress, Modal, Space } from "antd";
import intl from "react-intl-universal";
import styles from "./index.module.scss";
import classNames from "classnames";
import FileModal from "./fileModal";
import { useUploadQueue } from "@/contexts/uploadQueueContext";

const { Dragger } = Upload;

const isSameUploadFile = (file, task) => {
  if (!file || !task) {
    return false;
  }
  if (file.fileUuid && task.fileUuid) {
    return file.fileUuid === task.fileUuid;
  }
  return file.uid && task.uid && file.uid === task.uid;
};

const normalizeUploadFile = (item) => ({
  fileUuid: item.fileUuid,
  downloadAuthMode: item.downloadAuthMode,
  fileName: item.fileName,
  fileFormat: item.fileFormat || item.suffix?.replace(".", ""),
  fileSize: item.fileSize || item.size,
  filePath: item.filePath,
  fileRelativePath: item.fileRelativePath || item.fileName,
  name: item.name || item.fileName,
  key: item.fileUuid || item.key,
  uid: item.uid,
  suffix: item.suffix,
  size: item.size || item.fileSize,
  percent: item.percent,
  type: item.type,
  status: item.status,
});

const dedupeUploadFiles = (files) => {
  const fileMap = new Map();
  files.forEach((file) => {
    const key = file.fileUuid || file.uid;
    if (!key) {
      return;
    }
    fileMap.set(key, { ...(fileMap.get(key) || {}), ...file });
  });
  return Array.from(fileMap.values());
};

const UploadManage = ({ resourceUuid, fileData, onFileListChange }) => {
  const [fileList, setFileList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const {
    tasks,
    enqueueUpload,
    retryUpload,
    pauseUpload,
    resumeUpload,
    cancelUpload,
    removeUploadTask,
  } = useUploadQueue();

  useEffect(() => {
    const nextFileData = (fileData || []).map((item) => {
      const fileRelativePath = item.fileRelativePath || item.fileName;
      return {
        ...item,
        name: item.fileName,
        key: item.fileUuid,
        uid: item.fileUuid,
        suffix: "." + item.fileFormat,
        size: item.fileSize,
        percent: 100,
        webkitRelativePath: fileRelativePath?.includes("/") ? fileRelativePath : "",
        type: fileRelativePath?.includes("/") ? "folder" : "file",
      };
    });
    setFileList(nextFileData);
  }, [fileData]);

  useEffect(() => {
    const finishedTasks = tasks.filter(
      (task) => task.resourceUuid === resourceUuid && task.status === "done"
    );
    if (!finishedTasks.length) {
      return;
    }

    setFileList((prevList) => {
      let changed = false;
      const nextList = prevList.map((file) => {
        const task = finishedTasks.find((item) => isSameUploadFile(file, item));
        if (!task || file.status === "done") {
          return file;
        }
        changed = true;
        return { ...file, ...task };
      });

      const appendedTasks = finishedTasks.filter(
        (task) => !nextList.some((file) => isSameUploadFile(file, task))
      );
      if (appendedTasks.length) {
        changed = true;
      }

      const mergedList = dedupeUploadFiles(
        [...nextList, ...appendedTasks].map(normalizeUploadFile)
      );
      if (changed) {
        onFileListChange?.(mergedList);
      }

      return changed ? mergedList : prevList;
    });
  }, [onFileListChange, resourceUuid, tasks]);

  useEffect(() => {
    const canceledTasks = tasks.filter(
      (task) => task.resourceUuid === resourceUuid && task.status === "canceled"
    );
    if (!canceledTasks.length) {
      return;
    }

    setFileList((prevList) =>
      prevList.filter(
        (file) => !canceledTasks.some((task) => isSameUploadFile(file, task))
      )
    );
  }, [resourceUuid, tasks]);

  const addUploadFile = (file) => {
    setFileList((prevList) => {
      if (prevList.some((item) => item.uid === file.uid)) {
        return prevList;
      }
      return [...prevList, file];
    });
  };

  const buildUploadFile = (file, uid = file.uid) => {
    const fileRelativePath = file.webkitRelativePath || "";
    const fileName = file.name;
    const suffix = "." + fileName.split(".").pop();

    return {
      file,
      suffix,
      fileName,
      fileRelativePath,
      percent: 0,
      size: file.size,
      uid,
      status: "queued",
      type: fileRelativePath ? "folder" : "file",
    };
  };

  const handleUploadDirectoryChange = ({ file, fileList: list }) => {
    const originFileObj = list.find((item) => item.uid === file.uid)?.originFileObj;
    if (!originFileObj) {
      return false;
    }

    const newFile = buildUploadFile(originFileObj, file.uid);
    addUploadFile(newFile);
    enqueueUpload(resourceUuid, newFile);
    return false;
  };

  const handleUploadFilesChange = (info) => {
    const sourceFile = info.file.originFileObj || info.file;
    const newFile = buildUploadFile(sourceFile, info.file.uid);
    addUploadFile(newFile);
    enqueueUpload(resourceUuid, newFile);
    return false;
  };

  const retryUploadFile = (file) => {
    if (!file.file) {
      return;
    }
    retryUpload(file.id);
  };

  const cancelUploadFile = (file) => {
    if (!file.id) {
      return;
    }
    cancelUpload(file.id);
    setFileList((prevList) =>
      prevList.filter((item) => !isSameUploadFile(item, file))
    );
  };

  //通过字节大小显示单位
  const getSizeByBit = (bit) => {
    let res;
    if (bit < 1024) {
      res = bit + "Bytes";
    } else if (bit / 1024 < 1024) {
      res = (bit / 1024).toFixed(2) + "KB";
    } else if (bit / (1024 * 1024) < 1024) {
      res = (bit / (1024 * 1024)).toFixed(2) + "MB";
    } else {
      res = (bit / (1024 * 1024 * 1024)).toFixed(2) + "GB";
    }
    return res;
  };

  // 核心验证逻辑
  const beforeUpload = () => {
    return false;
  };

  // 图标选择逻辑
  const getFileIcon = (file) => {
    // 判断是否为文件夹（需要特殊处理）
    if (file.type === "folder" || file.fileRelativePath) {
      return <FolderOutlined />;
    }
    // 判断图片类型（支持所有 image/* MIME 类型）
    if (file?.file?.type?.startsWith("image/")) {
      return <FileImageOutlined />;
    }
    // 备用方案：通过文件扩展名判断（适用于某些浏览器不返回 MIME 类型的情况）
    const ext = file.suffix;
    const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp"];
    if (imageExtensions.includes(ext || "")) {
      return <FileImageOutlined />;
    }
    // 默认文件图标
    return <FileTextOutlined />;
  };

  const handleDraggerChange = (e) => {
    const currentFile = e.file.originFileObj || e.file;
    if (currentFile.webkitRelativePath) {
      handleUploadDirectoryChange(e);
    } else {
      handleUploadFilesChange(e);
    }
  };

  //显示文件管理弹窗
  const showFileManage = () => {
    setModalOpen(true);
  };
  const handleModalOk = () => {};
  const handleModalCancel = () => {
    setModalOpen(false);
  };
  const deleteFileById = (fileUuid) => {
    removeUploadTask(resourceUuid, fileUuid);
    setFileList((prevList) => {
      const nextList = dedupeUploadFiles(
        prevList.filter((item) => item.fileUuid !== fileUuid)
      );
      onFileListChange?.(nextList);
      return nextList;
    });
  };

  const currentFileUuids = fileList.map((item) => item.fileUuid).filter(Boolean);
  const queueFiles = tasks.filter(
    (item) =>
      item.resourceUuid === resourceUuid &&
      item.status !== "canceled" &&
      (item.status !== "done" ||
        !item.fileUuid ||
        currentFileUuids.includes(item.fileUuid))
  );
  const mergedFileList = fileList.map((file) => {
    const uploadTask = queueFiles.find((task) => isSameUploadFile(file, task));
    return uploadTask ? { ...file, ...uploadTask } : file;
  });
  const queueOnlyFiles = queueFiles.filter(
    (task) => !mergedFileList.some((file) => isSameUploadFile(file, task))
  );
  const visibleFileList = dedupeUploadFiles([...mergedFileList, ...queueOnlyFiles]);
  const uploadingFiles = queueFiles.filter((item) => item.file);
  const totalUploadSize = uploadingFiles.reduce((sum, item) => sum + item.size, 0);
  const totalUploadedSize = uploadingFiles.reduce(
    (sum, item) => sum + (item.size * (item.percent || 0)) / 100,
    0
  );
  const totalPercent = totalUploadSize
    ? Math.floor((totalUploadedSize / totalUploadSize) * 100)
    : 100;

  return (
    <div className={styles.uploadWrap}>
      <Modal
        title={intl.get("Share.Manage Files")}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        destroyOnHidden
        footer={null}
        width={1000}
        maskClosable={false}
      >
        <FileModal
          deleteFileById={deleteFileById}
          resourceUuid={resourceUuid}
          treeData={visibleFileList}
        />
      </Modal>
      <>
        <Dragger
          onChange={handleDraggerChange}
          multiple
          showUploadList={false}
          directory
          beforeUpload={beforeUpload}
          openFileDialogOnClick={false}
          style={{ cursor: "default" }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {intl.get("Drag and drop files or folders here to upload")}
          </p>
          <Upload
            showUploadList={false}
            directory
            beforeUpload={beforeUpload}
            onChange={handleUploadDirectoryChange}
          >
            <Button icon={<UploadOutlined />}>
              {intl.get("Upload Directory")}
            </Button>
          </Upload>
          <Upload
            showUploadList={false}
            multiple
            beforeUpload={beforeUpload}
            onChange={handleUploadFilesChange}
          >
            <Button icon={<UploadOutlined />}>
              {intl.get("Upload Files")}
            </Button>
          </Upload>
        </Dragger>
        <div className={styles.fileList}>
          <div className={styles.fileLength}>
            {visibleFileList.length} <span>files</span>
            {uploadingFiles.length > 0 && <span>{totalPercent}%</span>}
            <Button onClick={showFileManage}>
              <SettingFilled />
              {intl.get("Share.Manage Files")}
            </Button>
          </div>
          <div className={styles.fileWrap}>
            {visibleFileList.length
              ? visibleFileList.map((item) => {
                  return (
                    <div
                      key={item.fileUuid || item.uid}
                      className={classNames(
                        styles.fileItem,
                        "flex_box",
                        "align_center"
                      )}
                    >
                      <div className={styles.fileIcon}>{getFileIcon(item)}</div>
                      <div className={styles.fileContent}>
                        <p>
                          {item.fileRelativePath
                            ? item.fileRelativePath
                            : item.fileName}
                        </p>
                        <p className={styles.fileSize}>
                          {" "}
                          <span>{item.suffix}</span> {getSizeByBit(item.size)}
                        </p>
                      </div>
                      <div className={styles.progressWrap}>
                        {item.status === "error" && item.file ? (
                          <Space size={4}>
                            <Button size="small" onClick={() => retryUploadFile(item)}>
                              Retry
                            </Button>
                            <Button
                              size="small"
                              title="Cancel"
                              icon={<CloseCircleOutlined />}
                              onClick={() => cancelUploadFile(item)}
                            />
                          </Space>
                        ) : item.status === "paused" && item.file ? (
                          <Space size={4}>
                            <Progress type="circle" size={30} percent={item.percent} />
                            <Button
                              size="small"
                              title="Resume"
                              icon={<PlayCircleOutlined />}
                              onClick={() => resumeUpload(item.id)}
                            />
                            <Button
                              size="small"
                              title="Cancel"
                              icon={<CloseCircleOutlined />}
                              onClick={() => cancelUploadFile(item)}
                            />
                          </Space>
                        ) : ["queued", "hashing", "uploading"].includes(item.status) &&
                          item.file ? (
                          <Space size={4}>
                            <Progress type="circle" size={30} percent={item.percent} />
                            <Button
                              size="small"
                              title="Pause"
                              icon={<PauseCircleOutlined />}
                              onClick={() => pauseUpload(item.id)}
                            />
                            <Button
                              size="small"
                              title="Cancel"
                              icon={<CloseCircleOutlined />}
                              onClick={() => cancelUploadFile(item)}
                            />
                          </Space>
                        ) : (
                          <Progress type="circle" size={30} percent={item.percent} />
                        )}
                      </div>
                    </div>
                  );
                })
              : ""}
          </div>
        </div>
      </>
    </div>
  );
};

export default UploadManage;
